/*
 * bc-installed-diagram — "which webbing goes in this weblock, and which shackle
 * does it hang off" as an interactive 3D figure inside a product doc section.
 *
 * CHIPS PICK, A CARD SPEAKS. The hardware's OWN compatibility roster renders
 * twice, both server-rendered: as a strip of pill CHIPS leading the section
 * above the figure (swatch + name) that pick, and as DETAIL CARDS under the
 * figure (thumbnail, linked name,
 * quick-spec line, fitting note), one per compatible webbing, of which this
 * script shows exactly the pick's — everything else is `hidden`, so picking
 * swaps a card that already exists rather than building one. Choosing a chip
 * recolors the model's webbing to that product's diagram colour and, when the
 * pick changes the shackle it pairs with, SWAPS to the model of that pairing.
 * The MightyLock itself recolors with the PDP's selected anodize, from the same
 * `custom.model_3d` materials map the gallery model uses — one anodize spec,
 * two viewers.
 *
 * THE OVERLAY CARRIES THAT CHOICE TOO, as a row of anodize swatches — and they
 * are NOT a second picker: each one activates the PDP's own variant input for
 * its value, so the real change is Horizon's, the page behind the dialog moves
 * with it, and this element learns the result back through the same
 * `change` / `variant:update` listeners a click on the picker itself would
 * reach it by. Only when the page offers no control for that option (a
 * `hardware.option` override naming something that is not a variant option) is
 * a value kept here and painted view-only.
 *
 * Everything is data. The element reads one JSON config child, emitted by
 * snippets/bc-installed-diagram.liquid from the product's
 * `custom.installed_diagram` metafield plus the compatibility roster:
 *
 *   {"models":   {"<model key>": {"src": "<GLB url>", "alt": "…",
 *                                 "shackle": "<shackle key>",  // which pairing it shows
 *                                 "baked": true,               // its webbing is painted in
 *                                 "view": {"direction": [..], "up": [..],
 *                                          "distance": 1.6, "fov": 35},
 *                                 "poster": "<still url>"}},      // optional
 *    "fallbackModel": "<model key>",            // shown when a pairing has no GLB yet
 *    "webbingMaterials": ["webbing"],           // glTF materials painted with the pick
 *    "hardware":  {"option": "Color",           // the PDP option the metal follows
 *                  "materials": {"<glTF material>": {"<value>": "#hex", "*": "#hex"}},
 *                  "initial": "Black"},
 *    "shackles":  {"<key>": {"label": "BC Shackle SS", "pending": false}},
 *    "webbings":  {"<product handle>": {"title": "…", "hex": "#1FA8DC",
 *                                       "shackle": "<key>",
 *                                       "model": "<model key>"}},
 *    "selected":  "<product handle>",
 *    "autoRotate": false}
 *
 * TWO KINDS OF MODEL, and the difference is where the webbing's colour lives.
 * A model keyed by a SHACKLE is the pairing's generic one: its webbing is one
 * flat material, and this script paints the picked product's diagram colour onto
 * it. A model named by a webbing's own `model` is BAKED — built at that product's
 * real thickness and width with its printed cross-section laid over the strip, so
 * the colour is already there, in bands a single material cannot express. Baked
 * models are therefore NOT repainted (`baked: true`, and painting them would flood
 * the design away); the anodize recolor still applies to both, because the metal
 * is the same part either way.
 *
 * A pairing whose GLB does not exist yet is `models[key]` absent and
 * `shackles[key].pending` true. It still LISTS, still recolors, and still states
 * its pairing — the fallback model is shown with a caveat naming what is on
 * screen, because the part this page is selling (the lock, and the weave through
 * it) is identical in both pairings and only the shackle differs. Hiding the
 * figure would take the picture away to say less. A webbing whose BAKED model is
 * missing falls back the same way, to its pairing's generic model, and is painted
 * — which is the old behaviour exactly.
 *
 * WHY NOT assets/bc-model-3d.js: that viewer is a single stage RE-PARENTED
 * between the two hosts Horizon's gallery renders, cross-fading out of a poster
 * image and pinning the carousel's slide across variant re-renders — all of it
 * gallery lifecycle, none of which exists here. This one owns its own canvas in
 * a panel nothing morphs, and does the thing that one cannot: it swaps between
 * MODELS. The two share the pinned three.js, the free-tumble trackball and the
 * arm-to-interact rule, and are deliberately kept in step by eye.
 *
 * NOTHING IS FETCHED UNTIL THE PRESS. three.js, the meshopt decoder's WASM and
 * a ~550 KB GLB are most of a megabyte, and a WebGL context and a baked PMREM
 * environment on top of it — while this figure sits deep in a doc panel a
 * shopper scrolls PAST far more often than they stop at. So scrolling past it
 * costs nothing at all, and "View in 3D" is the one thing on the page that
 * starts any of it (Expand, which only exists once armed, loads too rather than
 * doing nothing; picking a chip before the press only moves the server-rendered
 * card and pairing line, which is what a shopper reading the roster asked for).
 * There is deliberately no IntersectionObserver BOOT here — the one observer in
 * this file starts only once a viewer exists and loads nothing: it tells the
 * render loop whether the figure is on screen. Same rule, same reasons, as
 * assets/bc-model-3d.js and assets/bc-rig-figure.js.
 *
 * UNTIL THE PRESS THE STAGE IS A STILL. A model's optional `poster` is a render
 * of that model in its own opening pose, through this viewer's own look, at the
 * stage's own 16:9 (tools/parts-diagram-tool/render_installed_poster.py), and
 * the snippet lays the initial pick's over the stage as an ordinary lazy <img>.
 * Three things happen to it here, in assets/bc-rig-figure.js's language:
 *
 *   - PICKING A CHIP SWAPS IT (#syncPoster) to the still of the model that pick
 *     would render — #modelFor's own answer, so still and model cannot disagree
 *     — which costs one image and still fetches nothing 3D.
 *   - THE PRESS CROSS-FADES IT AWAY: `data-bc-live` goes on with the first
 *     `ready`, and the stylesheet trades the still for the canvas. Once live the
 *     canvas IS the picture (a chip swap fades the stage exactly as it always
 *     has — the still does not blink back in between two models, because the
 *     shopper may have turned the model and the still has not).
 *   - × AND ESCAPE PUT IT BACK (#toStill): the pose goes home, the loop stops,
 *     the badge returns, and nothing is torn down — the next press is one
 *     frame. A tap OFF the figure only disarms, as before: giving the wheel
 *     back is one thing, taking the picture away from someone reading beside
 *     it is another.
 *
 * A model with no `poster` behaves exactly as it did before posters existed —
 * empty stage until the press, and a × that only disarms (there is no still to
 * go back to, and an empty box says less than a parked model).
 *
 * GESTURES, same contract as the gallery model: inert until ARMED, so a canvas
 * in the middle of a long doc panel can never eat a page scroll or a wheel. The
 * press that loads the model arms it (the shopper asked to handle the thing, so
 * they are not made to press twice). Armed = free tumble (drag), pan
 * (right-drag / shift-drag / two-finger drag), wheel and pinch zoom, plus Expand
 * into a full-window dialog. Tapping off it, or Escape, releases it.
 */
(() => {
  if (customElements.get('bc-installed-diagram')) return;

  /** Pinned three.js — same version, CDN and `/+esm` reasoning as bc-model-3d.js. */
  const THREE_BASE = 'https://cdn.jsdelivr.net/npm/three@0.165.0';
  const MODULE_URLS = [
    `${THREE_BASE}/+esm`,
    `${THREE_BASE}/examples/jsm/loaders/GLTFLoader.js/+esm`,
    `${THREE_BASE}/examples/jsm/environments/RoomEnvironment.js/+esm`,
  ];

  /**
   * The opening pose, as a direction from the part toward the camera plus a
   * screen-up vector in the model's own (Z-up CAD) frame — render_glb.py's VIEWS
   * convention, which transfers verbatim into the camera basis built below.
   * This default is the HANGING three-quarter (Jerry's pick, 2026-08-16): bow
   * up-right as it would hang off an anchor, the loaded strand leaving down-left
   * with its TOP face toward the viewer — the face the printed design lives on
   * (the old `hero` opened on the underside). Per model overridable through the
   * config's `view`.
   */
  const DEFAULT_DIRECTION = [0.53, 0.57, -0.63];
  const DEFAULT_UP = [-0.52, 0.8, 0.29];
  const DEFAULT_DISTANCE = 1.6;
  const DEFAULT_FOV = 35;

  /**
   * How many PARSED models stay resident: the one on stage plus the two picked
   * before it. Three, because comparing is what the chips are for — a shopper
   * goes back and forth between two or three webbings and A→B→A must not
   * refetch — while a full roster is fourteen pairings, and fourteen resident
   * scene graphs is millions of triangles of GPU buffers a mid-range phone
   * answers with a lost WebGL context. Anything older is disposed; picking it
   * again refetches, which the browser's HTTP cache almost always serves off
   * disk, so the cost of being wrong here is a parse, not a download.
   */
  const MODEL_CACHE_LIMIT = 3;

  /**
   * The meshopt decoder — what makes a `gltfpack -cc` GLB readable, pinned to
   * the EXACT version that packs them (gltfpack is meshoptimizer's own CLI, one
   * source tree, one version number; the file is `meshopt_decoder.mjs` since 1.0,
   * `meshopt_decoder.module.js` in 0.x). It matters most here: these assemblies
   * are the heavy models on the store — ~3.5 MB of raw CAD export each, ~800 KB
   * packed, ~550 KB over the wire — and a shopper comparing chips pulls several.
   *
   * No `/+esm`: that suffix exists to rewrite the three.js addons' bare `three`
   * import, and this module imports nothing, so the pinned file is already the
   * ES module we want.
   *
   * Same wiring and same reasoning as assets/bc-model-3d.js, deliberately —
   * the two viewers are kept in step by eye.
   */
  const MESHOPT_URL = 'https://cdn.jsdelivr.net/npm/meshoptimizer@1.2.0/meshopt_decoder.mjs';

  /**
   * The decoder, ready to hand to a loader — or null, read by every caller as
   * "parse without it". Its WASM is instantiated here, alongside the module
   * import and the first GLB's download, rather than in front of the first
   * parse. It FAILS SOFT (a CDN hiccup, or no WebAssembly, resolves to null)
   * because a rejection would take the whole figure down over a decoder the
   * uncompressed models never needed; a compressed GLB with no decoder still
   * fails properly, at GLTFLoader.parse, which #showModel already handles.
   */
  const loadMeshopt = () =>
    import(MESHOPT_URL).then(
      async (mod) => {
        const decoder = mod?.MeshoptDecoder;
        if (!decoder?.supported) return null;
        await decoder.ready;
        return decoder;
      },
      () => null
    );

  let libPromise = null;
  const loadLib = () =>
    (libPromise ||= Promise.all([...MODULE_URLS.map((url) => import(url)), loadMeshopt()]));

  const asVec = (value, fallback) =>
    Array.isArray(value) && value.length === 3 && value.every((n) => Number.isFinite(Number(n)))
      ? value.map(Number)
      : fallback;

  class BcInstalledDiagram extends HTMLElement {
    /** @type {any} parsed config child */ #config = null;
    /** @type {any} three.js module bag */ #three = null;
    /** @type {any} */ #viewer = null;
    /** @type {Map<string, any>} shackle key -> {scene, materials, home, dist} */ #models = new Map();
    /** @type {Map<string, Promise<any>>} in-flight loads, so a fast picker can't double-fetch */ #loading = new Map();
    /** @type {Map<string, Promise<ArrayBuffer>>} GLB bytes, held only until parsed */ #bytes = new Map();
    /** @type {string[]} loaded keys, least recently picked FIRST — #trim evicts along it */ #order = [];
    /** @type {string} */ #modelKey = '';
    /** @type {string} */ #handle = '';
    /**
     * A colour chosen from the overlay's swatches that the PAGE could not be
     * told about — set only when no variant input for the option exists to
     * activate, and read only after a live picker read comes back empty, so a
     * real picker always wins and this can never desync the figure from the
     * page.
     * @type {string}
     */
    #hardwareOverride = '';
    /** @type {IntersectionObserver|null} */ #observer = null;
    /** @type {Promise<any>|null} */ #boot = null;
    /** Guards against an impatient picker: only the newest swap may finish. */ #showToken = 0;
    #armed = false;
    /**
     * A press asked for the model and the model is not here yet. Held rather
     * than acted on because a load can take seconds, and cleared by Escape, by
     * a tap off the figure and by the disarm × — so a shopper who pressed and
     * scrolled on does not get a figure arming itself (and seizing the wheel)
     * somewhere above them when the GLB lands.
     */
    #wantArm = false;
    /**
     * Is any part of this figure near the viewport? Read by the render loop —
     * both by the idle auto-rotate and by whether the loop runs at all. True
     * until an observer says otherwise, so nothing depends on the first
     * callback having run.
     */
    #onScreen = true;
    #expanded = false;
    #failed = false;
    /**
     * The × (or Escape) has put the STILL back over a loaded viewer — see
     * #toStill. Everything 3D is kept and parked; the figure reads as it did
     * before the first press, and the next press comes back through #toModel.
     */
    #stillOnly = false;
    /** The next model mounted opens at its authored pose (set by #toStill). */
    #rehome = false;

    connectedCallback() {
      if (this.#config) {
        // A re-connect (or a node move): everything is already built, and the
        // canvas is still a child of this element, so its GL context and its
        // parsed GLBs come along with it. Only the visibility observer has to
        // be re-established — and only if there is a live viewer for it to
        // quiet — and the loop, which the disconnect stopped, restarted.
        if (this.#viewer && !this.#observer) this.#watchVisibility();
        this.syncRenderLoop();
        return;
      }
      try {
        this.#config = JSON.parse(this.querySelector('script[data-bc-installed-diagram]')?.textContent || '');
      } catch (_e) {
        return; // malformed config — the server-rendered list stands on its own
      }
      if (!this.#config?.webbings || !this.#config?.models) {
        this.#config = null;
        return;
      }

      this.#handle = this.#config.selected || Object.keys(this.#config.webbings)[0] || '';

      // Tells the stylesheet a viewer exists: it is what shows the figure, what
      // brings the chip picker into the flow, and what collapses the card list
      // to the pick. Without JS (or with a malformed config, or before this
      // runs) the section is the server-rendered compatibility list alone —
      // every card under its shackle's heading, no chips, no dead controls —
      // which is also what #fail() drops back to.
      this.dataset.bcJs = '';

      // The server rendered all of this for the initial pick already; running it
      // once here is what guarantees the two agree, whatever the config says.
      this.#syncChrome();

      this.addEventListener('click', this.#onClick);
    }

    /**
     * THE ONE OBSERVER IN THIS FILE, and it loads nothing — it flips a flag the
     * render loop reads. Started when a viewer is built, so a figure nobody
     * pressed never has one at all, which keeps "nothing happens until the
     * press" true of the observer as well as of the bytes.
     *
     * Without it a loaded figure keeps a 60 fps callback (and, with auto-rotate
     * on, a spinning re-render) running for the rest of the session, several
     * sections above wherever the shopper currently is.
     */
    #watchVisibility() {
      if (this.#observer || typeof IntersectionObserver !== 'function') return;
      this.#observer = new IntersectionObserver(
        (entries) => {
          const on = entries.some((entry) => entry.isIntersecting);
          if (on === this.#onScreen) return;
          this.#onScreen = on;
          // Coming back into view: the loop has been stopped, so flag a frame
          // for it to draw as it starts (an auto-rotate has been skipping its
          // rotation, and a repaint may have been asked for while it was off).
          if (on) this.#viewer?.invalidate();
          this.syncRenderLoop();
        },
        { rootMargin: '200px 0px' }
      );
      this.#observer.observe(this);
    }

    /**
     * Run the render loop only while there is something to render TO: the
     * figure near the viewport (or its overlay open) and the tab in front.
     *
     * Everything else survives — the canvas stays a child of this element, so a
     * re-connect (or a morph that moves the node) resumes with its GL context
     * and its loaded GLBs, and `renderer.setAnimationLoop(null)` is only ever
     * the loop stopping, never the viewer being torn down. Public because the
     * document-level `visibilitychange` listener at the foot of this file calls
     * it on every figure.
     */
    syncRenderLoop() {
      const wanted =
        !this.#failed &&
        !this.#stillOnly &&
        this.isConnected &&
        (this.#onScreen || this.#expanded) &&
        document.visibilityState !== 'hidden';
      this.#viewer?.setLoopRunning(wanted);
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
      this.#observer = null;
      // Nothing on screen can be this element's any more, so the loop stops
      // here: the observer above is the only thing that would have stopped it,
      // and it has just been disconnected. Everything else stays — the canvas
      // is a child of this element, so a re-connect resumes with its GL context
      // and its loaded GLBs, and syncRenderLoop starts the loop again.
      this.syncRenderLoop();
    }

    /* ---------------------------------------------------------------- *
     * The viewer
     * ---------------------------------------------------------------- */

    /** THE ENTRY POINT: the shopper has asked for the model (see #onClick — an
     *  arm press, an expand press, or a chip picked on an already-live figure;
     *  nothing else in this file calls it). Builds the viewer once and shows
     *  whatever is picked WHEN it is ready — concurrent callers share the one
     *  promise, and every one of them ends up on the current pick.
     *  @returns {Promise<any>} the viewer, or null if it could not be built. */
    #build() {
      // A figure that has already failed has nothing to retry with: its bytes
      // were dropped and the stylesheet has taken its whole viewer away.
      if (this.#failed) return Promise.resolve(null);
      // The first GLB's BYTES and the three.js module graph are independent
      // network work — a couple of MB off the Shopify CDN and an ESM graph off
      // jsDelivr — and the first parse needs both, so they start together
      // rather than the download queueing behind the import.
      //
      // Only on the very FIRST call: a `#boot` that already exists means those
      // bytes are in flight, parsed, or long since released, and asking again
      // here would be a second download of the same file (arming the figure and
      // picking a chip both call back into #build).
      if (!this.#boot) {
        // Said HERE rather than in #showModel, which is a module import away:
        // the press has to be answered now, and this is what puts "Loading…"
        // in the expand overlay and `aria-busy` on the badge.
        this.#setState('loading');
        const { key } = this.#modelFor(this.#handle);
        if (key) this.#fetchGlb(key);
      }
      this.#boot ||= this.#buildViewer()
        .then((viewer) => {
          if (!viewer) this.#fail();
          return viewer;
        })
        .catch(() => {
          this.#fail();
          return null;
        });
      return this.#boot.then((viewer) => {
        if (!viewer) return null;
        return this.#showModel(this.#handle).then(() => viewer);
      });
    }

    /**
     * The figure's own state, in one place because two things hang off it: the
     * stylesheet (the stage fades in at `ready`, the overlay says "Loading…" at
     * `loading`, the cards come back at `error`) and the arm badge's
     * `aria-busy`, which is how a screen reader hears the press being answered
     * — the badge is a button whose label does not change while it works.
     */
    #setState(value) {
      // A load that lands AFTER the × must not take the still away again: the
      // model is mounted and kept (that work is done and worth having), but
      // the figure stays the picture the shopper asked to go back to.
      if (value === 'ready' && this.#stillOnly) value = '';
      if (value) this.dataset.bcState = value;
      else delete this.dataset.bcState;
      // What trades the still for the canvas. Set by the first `ready` and
      // taken off only by #toStill — NOT by a later `loading`, so a chip swap
      // on a live figure fades the stage as it always has rather than blinking
      // the (un-turned) still in between two (turned) models.
      if (value === 'ready') this.dataset.bcLive = '';
      for (const button of this.querySelectorAll('[data-bc-installed-arm]')) {
        if (value === 'loading') button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      }
    }

    /** No WebGL, or a GLB that would not load: drop back to the list-only look
     *  — dropping `data-bc-js` also hands back the cards #syncChrome hid (the
     *  stylesheet un-hides them without it) and takes the chip strip away. */
    #fail() {
      this.#failed = true;
      delete this.dataset.bcJs;
      this.#setState('error');
      this.#wantArm = false;
      this.#setArmed(false);
      // The canvas is `display: none` from here on (the stylesheet hangs the
      // whole viewer off `data-bc-js`), so the loop has nothing left to draw
      // to. This is the one true teardown in the file: setAnimationLoop(null),
      // and it is never started again.
      this.#viewer?.setLoopRunning(false);
      // Nothing will parse them now — and a machine with no WebGL is exactly
      // the one that should not be holding a GLB's worth of ArrayBuffer for the
      // rest of the session. Any load still in flight kept its own reference to
      // its bytes, so this only drops the map's claim on them.
      this.#bytes.clear();
    }

    async #buildViewer() {
      const [THREE, gltfMod, roomMod, meshopt] = await loadLib();
      this.#three = { THREE, gltfMod, roomMod, meshopt };

      const stage = this.querySelector('[data-bc-installed-stage]');
      if (!stage) return null;

      let renderer;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      } catch (_e) {
        return null;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      // Neutral (Khronos PBR), not ACES filmic: ACES darkens and desaturates
      // saturated albedos, which read the baked webbing designs' vivid store
      // hexes back as muted pastels. Neutral keeps the colour near the authored
      // hex — the whole point of building the wraps at the products' real designs.
      renderer.toneMapping = THREE.NeutralToneMapping;
      renderer.domElement.setAttribute('aria-hidden', 'true');
      stage.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      try {
        // The generator and the room are SCAFFOLDING — a set of intermediate
        // render targets, a pile of blur materials and a boxed-up room, all of
        // it spent the instant the cubemap is baked. Only the texture outlives
        // this block, and it has to: it is what the anodized metal reflects, so
        // dropping the scaffolding changes nothing on screen and hands back a
        // few MB of GPU memory that would otherwise be held for the session.
        const pmrem = new THREE.PMREMGenerator(renderer);
        const room = new roomMod.RoomEnvironment();
        scene.environment = pmrem.fromScene(room, 0.04).texture;
        pmrem.dispose();
        this.#disposeTree(room);
      } catch (_e) {
        /* the explicit lights below carry the scene on their own */
      }
      scene.add(new THREE.HemisphereLight(0xffffff, 0x444455, 1.2));
      const key = new THREE.DirectionalLight(0xffffff, 1.6);
      key.position.set(60, 80, 120);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0xffffff, 0.6);
      fill.position.set(-80, -40, -100);
      scene.add(fill);

      const camera = new THREE.PerspectiveCamera(DEFAULT_FOV, 1, 0.01, 2000);
      const pivot = new THREE.Group();
      scene.add(pivot);

      let dist = 1;
      let minDist = 0.1;
      let maxDist = 100;
      let panLimit = Infinity;
      let homeDist = 1;
      let dirty = true;
      camera.position.set(0, 0, dist);

      /**
       * A frame is WANTED. Every "something moved" in this viewer goes through
       * here rather than assigning `dirty` directly, because the loop is not
       * always running (see setLoopRunning): the flag on its own would be a
       * repaint that never happens. Starting it is conditional on the figure
       * being somewhere it can be seen — this element's own rule, asked for
       * rather than reimplemented, so an invalidate from an off-screen repaint
       * (a variant recolor several sections above the shopper) sets the flag
       * and leaves the loop stopped until the observer says otherwise.
       */
      const mark = () => {
        dirty = true;
        this.syncRenderLoop();
      };

      /** Is the rAF callback installed? See setLoopRunning. */
      let looping = false;

      const viewer = {
        stage,
        renderer,
        scene,
        camera,
        pivot,
        autoRotate:
          Boolean(this.#config.autoRotate) && !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
        invalidate: mark,
        /**
         * Install or drop the rAF callback.
         *
         * Render-on-demand already keeps the GPU idle, but the CALLBACK is not
         * free: it runs 60 times a second for the life of the page, and this
         * element deliberately survives disconnects and morphs with its context
         * intact, so "the life of the page" is exactly what it means. Stopping
         * it is `setAnimationLoop(null)` — the renderer, the context, the
         * environment and every parsed model stay exactly as they are, so
         * starting again is one call and one frame, with nothing to rebuild.
         *
         * The clock is read and thrown away on the way in: `getDelta` measures
         * from the last read, so an idle stretch would otherwise arrive at the
         * auto-rotate as one enormous delta and snap the model round.
         */
        setLoopRunning(on) {
          if (on === looping) return;
          looping = on;
          if (!on) {
            renderer.setAnimationLoop(null);
            return;
          }
          clock.getDelta();
          renderer.setAnimationLoop(frame);
        },
        /** Distance, clip planes and limits from the model's own size — these
         *  GLBs are in metres and a 220 mm assembly is 0.22 across. */
        frame(size, distanceFactor, fov) {
          camera.fov = fov;
          dist = size * distanceFactor;
          homeDist = dist;
          minDist = size * 0.4;
          maxDist = size * 12;
          // 1.5 bounding RADII off centre, so a pan can never lose the model.
          panLimit = size * 0.75;
          camera.near = size / 100;
          camera.far = size * 100;
          camera.position.set(0, 0, dist);
          pivot.position.set(0, 0, 0);
          camera.updateProjectionMatrix();
          mark();
        },
        homeFraming() {
          dist = homeDist;
          camera.position.set(0, 0, dist);
          pivot.position.set(0, 0, 0);
          mark();
        },
        resize() {
          const width = stage.clientWidth;
          const height = stage.clientHeight;
          if (!width || !height) return;
          renderer.setSize(width, height);
          camera.aspect = width / height;
          camera.updateProjectionMatrix();
          // Redraw here rather than flagging the loop: setSize reallocates (and
          // so clears) the drawing buffer, and the ResizeObserver runs after the
          // frame's rAF callbacks — a deferred redraw would paint one empty frame.
          dirty = false;
          renderer.render(scene, camera);
        },
        zoomBy(factor) {
          dist = Math.min(maxDist, Math.max(minDist, dist * factor));
          camera.position.setLength(dist);
          mark();
        },
        /** Pan in CANVAS PIXELS: the model slides in the camera's screen plane
         *  and the pointer keeps hold of the point it grabbed. Moving the pivot
         *  (never the camera) keeps screen right/up equal to world +X/+Y. */
        panBy(dxPixels, dyPixels) {
          const perPixel =
            (2 * dist * Math.tan((camera.fov * Math.PI) / 360)) / (renderer.domElement.clientHeight || 1);
          pivot.position.set(pivot.position.x + dxPixels * perPixel, pivot.position.y - dyPixels * perPixel, 0);
          const offset = Math.hypot(pivot.position.x, pivot.position.y);
          if (offset > panLimit) pivot.position.multiplyScalar(panLimit / offset);
          mark();
        },
        /** Free tumble: the model turns about the CAMERA's screen axes, so there
         *  is no pole to stick at and no upright it snaps back to. */
        tumble(dx, dy) {
          const q = new THREE.Quaternion()
            .setFromAxisAngle(new THREE.Vector3(0, 1, 0), dx)
            .multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), dy));
          pivot.quaternion.premultiply(q);
          mark();
        },
      };

      new ResizeObserver(() => viewer.resize()).observe(stage);

      const clock = new THREE.Clock();
      const frame = () => {
        const delta = clock.getDelta();
        // Render-on-demand: the loop runs, the GPU only works when something
        // moved — and the one thing that moves on its own is skipped entirely
        // while the figure is off screen, so an idle spin cannot burn a phone's
        // battery several sections above the shopper. (Belt and braces with
        // setLoopRunning, which stops the loop outright in that case; this is
        // what covers the frames between going off screen and the observer
        // saying so.)
        if (viewer.autoRotate && this.#onScreen && !this.#armed && !this.#expanded) {
          pivot.rotateOnWorldAxis(new THREE.Vector3(0, 1, 0), delta * 0.35);
          // Straight to the flag: the loop is running by definition in here, so
          // going through mark() would ask to start it once a frame forever.
          dirty = true;
        }
        if (!dirty) return;
        dirty = false;
        renderer.render(scene, camera);
      };
      viewer.setLoopRunning(true);

      this.#viewer = viewer;
      this.#wireGestures(viewer);
      this.#watchVisibility();
      // Only now can the element's own rule be applied — setLoopRunning above
      // started the loop against a null #viewer, which is what draws the first
      // frame; this stops it again if the figure is somehow already off screen
      // or the tab has gone to the background mid-load.
      this.syncRenderLoop();
      return viewer;
    }

    /* ---------------------------------------------------------------- *
     * Models
     * ---------------------------------------------------------------- */

    /** The shackle a webbing pairs with, defaulted so an unknown pick is never fatal. */
    #shackleFor(handle) {
      const entry = this.#config.webbings?.[handle];
      const wanted = entry?.shackle;
      if (wanted && this.#config.shackles?.[wanted]) return wanted;
      return this.#config.fallbackModel || Object.keys(this.#config.models || {})[0] || '';
    }

    /** The pairing a model shows: its own declaration, else its key IS a shackle. */
    #modelShackle(key) {
      return this.#config.models?.[key]?.shackle || key;
    }

    /**
     * Which model a pick actually renders, in preference order: the webbing's own
     * baked one, then its pairing's generic one, then the fallback, then whatever
     * exists. `satisfied` is false only when what ends up on screen shows a
     * DIFFERENT pairing — which is what the caveat is for, and why it compares
     * shackles rather than keys (a baked model's key is never its shackle's).
     */
    #modelFor(handle) {
      const shackle = this.#shackleFor(handle);
      const models = this.#config.models || {};
      const baked = this.#config.webbings?.[handle]?.model;
      const key =
        (baked && models[baked]?.src && baked) ||
        (models[shackle]?.src && shackle) ||
        (models[this.#config.fallbackModel]?.src && this.#config.fallbackModel) ||
        Object.keys(models).find((k) => models[k]?.src) ||
        '';
      return { key, shackle, satisfied: Boolean(key) && this.#modelShackle(key) === shackle };
    }

    /**
     * A model's BYTES, kept apart from parsing them because the two wait on
     * different things: the download needs only the network, the parse needs
     * three.js to have finished importing. Splitting them is what lets #build
     * start the first GLB in the same tick as the module import.
     *
     * The map is a HANDOFF, not a cache: an entry lives from the moment the
     * fetch starts until the moment it is parsed (or fails), and never a turn
     * longer — a multi-MB ArrayBuffer sitting in a map is the same leak this
     * file's model cap exists to prevent. Re-picking an evicted model refetches
     * the bytes, which is a browser HTTP-cache read.
     *
     * Both callers reach a key through #modelFor (which only ever names a model
     * that HAS a `src`) or past #loadModel's own spec guard, so the URL is there.
     */
    #fetchGlb(key) {
      const held = this.#bytes.get(key);
      if (held) return held;
      const url = this.#config.models?.[key]?.src;
      const bytes = fetch(url, { credentials: 'omit' }).then((response) => {
        // GLTFLoader's own FileLoader treats a 404 as an error; fetch treats it
        // as a response, so the status check is what keeps the two equivalent
        // (an error page parsed as a GLB would fail anyway, just later and with
        // a stranger message).
        if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
        return response.arrayBuffer();
      });
      // A PREFETCH may sit here for a moment with nobody awaiting it, and an
      // unhandled rejection would log to the console for a failure the real
      // consumer is about to handle properly. This marks it handled without
      // consuming it — every consumer still awaits `bytes` itself.
      bytes.catch(() => {});
      this.#bytes.set(key, bytes);
      return bytes;
    }

    #loadModel(key) {
      const cached = this.#models.get(key);
      if (cached) {
        this.#touch(key);
        return Promise.resolve(cached);
      }
      const inFlight = this.#loading.get(key);
      if (inFlight) return inFlight;

      const { THREE, gltfMod, meshopt } = this.#three;
      const spec = this.#config.models?.[key];
      if (!spec?.src) return Promise.reject(new Error(`no model for ${key}`));
      const bytes = this.#fetchGlb(key);
      // Identity-checked, so a retry that has already started its own fetch is
      // never cleared out from under itself by the previous attempt's cleanup.
      const release = () => {
        if (this.#bytes.get(key) === bytes) this.#bytes.delete(key);
      };
      const promise = bytes
        .then(
          (buffer) =>
            new Promise((resolve, reject) => {
              // parse(), not load(): the bytes are already here. The second
              // argument is the base URL any external resource inside the file
              // resolves against — these GLBs are self-contained, but a sibling
              // .bin or texture still resolves exactly as load() made it, since
              // this is three's own extractUrlBase spelled out.
              const base = spec.src.slice(0, spec.src.lastIndexOf('/') + 1) || './';
              const loader = new gltfMod.GLTFLoader();
              // Reached only by a GLB that declares EXT_meshopt_compression, so
              // the raw models parse exactly as they did (see MESHOPT_URL).
              if (meshopt) loader.setMeshoptDecoder(meshopt);
              loader.parse(buffer, base, resolve, reject);
            })
        )
        .then((gltf) => {
          const model = gltf.scene;
          const box = new THREE.Box3().setFromObject(model);
          model.position.sub(box.getCenter(new THREE.Vector3()));
          const size = box.getSize(new THREE.Vector3()).length() || 1;

          // bc: the webbing's printed cross-section rides as skins standing 0.02 mm
          // (sewn-loop rig) / 0.004 mm (wrap rigs) off the solid body named
          // `webbing`. Zoomed out, that is under the depth buffer's resolution and
          // the body fights through the skins — purple streaks across a green
          // bottom face on a two-tone webbing. Push the BODY back two depth units
          // (plus one slope unit) so the skins always win; the skins and threads are
          // left alone, so nothing else in the model moves.
          model.traverse((node) => {
            const material = /** @type {any} */ (node).material;
            for (const entry of Array.isArray(material) ? material : [material]) {
              if (entry?.name !== 'webbing') continue;
              entry.polygonOffset = true;
              entry.polygonOffsetFactor = 1;
              entry.polygonOffsetUnits = 2;
              entry.needsUpdate = true;
            }
          });

          /** Materials by NAME — the whole recolor contract. */
          const materials = new Map();
          model.traverse((node) => {
            const material = /** @type {any} */ (node).material;
            if (!material) return;
            for (const entry of Array.isArray(material) ? material : [material]) {
              if (entry?.color && entry.name && !materials.has(entry.name)) materials.set(entry.name, entry);
            }
          });

          // render_glb.py names a view by the direction from the part toward the
          // camera plus a screen-up vector, both in the model's own frame. Its
          // camera_axes turns the pair into the model-space vectors that map to
          // screen right / up / toward-viewer; the rotation we want takes those
          // onto the world axes the camera sits on — that basis, transposed.
          const view = spec.view || {};
          const zDir = new THREE.Vector3(...asVec(view.direction, DEFAULT_DIRECTION)).normalize();
          const xDir = new THREE.Vector3(...asVec(view.up, DEFAULT_UP)).cross(zDir).normalize();
          const yDir = new THREE.Vector3().crossVectors(zDir, xDir);
          const home = new THREE.Quaternion().setFromRotationMatrix(
            new THREE.Matrix4().makeBasis(xDir, yDir, zDir).transpose()
          );

          const entry = {
            model,
            materials,
            home,
            size,
            distance: Number(view.distance) || DEFAULT_DISTANCE,
            fov: Number(view.fov) || DEFAULT_FOV,
          };
          this.#models.set(key, entry);
          this.#touch(key);
          this.#loading.delete(key);
          release();
          return entry;
        });
      // A failed fetch must not poison the key: without this the rejected
      // promise stays in the map and every later pick of that pairing replays
      // the same failure without ever retrying — and the bytes go with it, so
      // the retry is a real retry rather than a re-parse of whatever came back.
      promise.catch(() => {
        this.#loading.delete(key);
        release();
      });
      this.#loading.set(key, promise);
      return promise;
    }

    /* ---------------------------------------------------------------- *
     * Keeping the cache small
     * ---------------------------------------------------------------- */

    /** Most recently picked LAST — #order is only ever read back to front by #trim. */
    #touch(key) {
      const at = this.#order.indexOf(key);
      if (at !== -1) this.#order.splice(at, 1);
      this.#order.push(key);
    }

    /**
     * Bring the cache back to MODEL_CACHE_LIMIT, oldest pick first, and hand the
     * evicted models' GPU memory back.
     *
     * Two things are never evicted: the model ON STAGE, and any key a load is
     * still filling in (an in-flight key is not in #models yet, so that guard is
     * belt-and-braces — it is here so the rule reads as the rule). Nothing else
     * needs protecting, because #trim only ever runs from the #showModel call
     * that holds the CURRENT token: any other pending show is by definition a
     * stale one, and a stale show returns at its token check before it so much
     * as looks at the entry it was awaiting. Running it from the LOAD instead
     * would give that guarantee up (a load lands whenever the network says so,
     * possibly under a newer pick) to save holding one extra model until the
     * next pick — the cap is a memory ceiling, not an exact size.
     */
    #trim() {
      for (let i = 0; i < this.#order.length; ) {
        const key = this.#order[i];
        const entry = this.#models.get(key);
        if (!entry) {
          this.#order.splice(i, 1); // already gone; keep the order list honest
          continue;
        }
        if (this.#models.size <= MODEL_CACHE_LIMIT) break;
        if (key === this.#modelKey || this.#loading.has(key)) {
          i++;
          continue;
        }
        this.#models.delete(key);
        this.#order.splice(i, 1);
        this.#disposeTree(entry.model);
        entry.materials.clear();
      }
      // A prefetch the shopper picked past — #build starts the initial GLB's
      // bytes before the viewer exists, and a chip tapped in that window sends
      // #showModel somewhere else, stranding a few MB nobody will parse. Bytes
      // still attached to a live load are not that (and are released by the
      // parse itself the moment it lands).
      for (const key of this.#bytes.keys()) if (!this.#loading.has(key)) this.#bytes.delete(key);
    }

    /**
     * Give a retired model's GPU memory back. three uploads geometry buffers and
     * textures lazily and holds them until it is TOLD to let go: dropping the
     * last JS reference to a scene frees the JS objects and none of the VRAM.
     *
     * Geometries and materials are shared BETWEEN meshes inside a GLB, so the
     * walk carries a `seen` set — double-disposing is harmless in three, but the
     * set is also what makes one pass over a shared texture enough. Texture
     * slots are ordinary material properties (map, normalMap, roughnessMap, …)
     * that vary by material type, so they are found by shape rather than from a
     * hardcoded list, which also catches whatever a future GLB carries.
     *
     * Deliberately NOT disposed: the scene environment. It is the renderer's,
     * baked once at build time, and every model that follows this one reflects
     * it — so it is skipped by identity even if a material points at it.
     */
    #disposeTree(root) {
      const environment = this.#viewer?.scene.environment || null;
      const seen = new Set();
      root.removeFromParent?.();
      root.traverse((node) => {
        const geometry = /** @type {any} */ (node).geometry;
        if (geometry?.dispose && !seen.has(geometry)) {
          seen.add(geometry);
          geometry.dispose();
        }
        const material = /** @type {any} */ (node).material;
        if (!material) return;
        for (const one of Array.isArray(material) ? material : [material]) {
          if (!one || seen.has(one)) continue;
          seen.add(one);
          for (const value of Object.values(one)) {
            if (value?.isTexture && value !== environment && !seen.has(value)) {
              seen.add(value);
              value.dispose();
            }
          }
          one.dispose?.();
        }
      });
    }

    /**
     * Put a pick's model on screen. Orientation is the shopper's: only the FIRST
     * model of the session opens at its authored pose, because a swap is the same
     * weave in another webbing (or on another shackle) and snapping the view back
     * would read as the figure resetting itself under them.
     */
    async #showModel(handle) {
      const { key } = this.#modelFor(handle);
      if (!key || !this.#viewer) {
        // #build() flags 'loading' before it can know whether the pick has a
        // model at all; don't leave the figure (and its aria-busy badge)
        // wedged in that state when there is nothing to show.
        if (this.dataset.bcState === 'loading') this.#setState(this.#modelKey ? 'ready' : 'error');
        return;
      }
      if (key === this.#modelKey) {
        // A pick that lands on the model already on screen OWNS the stage just
        // like a swap would: bump the token so an older pick's still-in-flight
        // GLB cannot finish and shove its model under this chip, and restore
        // 'ready' in case that older pick had faded the stage for its fetch.
        // (Two picks share a key only on the GENERIC models — a baked one is one
        // webbing's — and there the repaint below is exactly what has to happen.)
        this.#showToken++;
        this.#touch(key);
        this.#setState('ready');
        this.#paint();
        return;
      }

      const token = ++this.#showToken;
      // Only a model that actually has to be fetched drops the 'ready' flag —
      // the stage fades while it loads. A cached one resolves in a microtask,
      // and flagging it would blink the figure on every pick.
      if (!this.#models.has(key)) this.#setState('loading');

      let entry;
      try {
        entry = await this.#loadModel(key);
      } catch (_e) {
        // Keep whatever is on screen: a pairing whose GLB will not load is
        // exactly the case the caveat covers, and an empty stage says less.
        // Nothing on screen at all means the figure never worked — hand the
        // section back to the server-rendered pairing list.
        if (token === this.#showToken) {
          if (this.#modelKey) this.#setState('ready');
          else this.#fail();
        }
        return;
      }
      // A pick made while this GLB was in flight owns the stage now.
      if (token !== this.#showToken) return;
      const previous = this.#modelKey ? this.#models.get(this.#modelKey) : null;
      if (previous) this.#viewer.pivot.remove(previous.model);
      this.#viewer.pivot.add(entry.model);
      // …or the first model back from the still, which is a picture of THIS
      // model's authored pose: the cross-fade only works onto the same view.
      if (!previous || this.#rehome) this.#viewer.pivot.quaternion.copy(entry.home);
      this.#rehome = false;
      this.#viewer.frame(entry.size, entry.distance, entry.fov);
      this.#modelKey = key;
      // After #modelKey moves, never before: what is on stage is what #trim
      // refuses to evict, and evicting the model this call just mounted would
      // dispose the buffers the very next render draws from.
      this.#trim();
      this.#paint();
      this.#viewer.resize();
      // Held back until the first paint so the raw CAD grey is never shown.
      this.#setState('ready');
    }

    /**
     * Paint the current pick onto the live model: the chosen webbing's own
     * diagram colour on the webbing material(s), and the PDP's selected anodize
     * on the metal, from the same materials map the gallery model uses. A
     * material this GLB does not carry, or a value with no entry, is left
     * exactly as authored — an unknown selection never half-repaints the model.
     *
     * A BAKED model is skipped for the webbing half and only the webbing half:
     * it was built as this product, printed cross-section and all, so its strip
     * already carries several materials and flooding one of them with the line
     * colour would erase the design. The metal is the same part in every model,
     * so the anodize is painted either way.
     *
     * `Color.set('#rrggbb')` rather than any manual conversion: three 0.165 has
     * ColorManagement on, so it reads the hex as sRGB, and the renderer's sRGB
     * output converts back — the pixel matches the hex.
     */
    #paint() {
      const entry = this.#models.get(this.#modelKey);
      if (!entry) return;

      const hex = this.#config.models?.[this.#modelKey]?.baked
        ? null
        : this.#config.webbings?.[this.#handle]?.hex;
      if (typeof hex === 'string' && hex) {
        for (const name of this.#config.webbingMaterials || []) {
          const material = entry.materials.get(name);
          if (!material) continue;
          try {
            material.color.set(hex);
          } catch (_e) {
            /* leave this material as authored */
          }
        }
      }

      const hardware = this.#config.hardware;
      if (hardware?.materials) {
        const value = this.#hardwareValue();
        for (const [name, colors] of Object.entries(hardware.materials)) {
          const material = entry.materials.get(name);
          if (!material || !colors) continue;
          const paint = (value != null && colors[value]) || colors['*'];
          if (typeof paint !== 'string' || !paint) continue;
          try {
            material.color.set(paint);
          } catch (_e) {
            /* leave this material as authored */
          }
        }
      }

      this.#viewer?.invalidate();
    }

    /**
     * The PDP's selected value for the option the metal follows, read from the
     * MAIN product form through the shared resolver exactly as bc-model-3d and
     * bc-variant-recolor do (quick-add pickers excluded — a modal selection is a
     * different product context). Falls back to a colour chosen from the
     * overlay's swatch row that the page had no control to receive, then to the
     * value Liquid rendered for the initially selected variant, which also
     * covers the resolver not having parsed yet.
     *
     * ORDER IS THE WHOLE POINT: the live read first, always. The overlay's
     * swatches normally never set the override at all (they activate the real
     * input and the read below returns their value a moment later), so it
     * cannot pin the figure to a colour the page has since moved off.
     */
    #hardwareValue() {
      // Optional all the way down: `refresh()` reaches this from a document
      // listener, which fires on every element on the page including one whose
      // config would not parse (`#config` null, figure never built, server
      // list standing on its own) — and a throw there would take the OTHER
      // listeners on that event down with it.
      const option = this.#config?.hardware?.option;
      const resolved = option
        ? window.bcVariantResolve?.readSelectedOption(document, option, { exclude: 'quick-add-dialog' })
        : null;
      return resolved || this.#hardwareOverride || this.#config?.hardware?.initial || null;
    }

    /**
     * Pick an anodize from the overlay's swatch row.
     *
     * THE PDP'S PICKER IS THE ONE THAT DECIDES. Activating its input for this
     * value hands the change to Horizon's own flow — the checked pill, the
     * variant in the URL, the price, the sticky bar, what the Add-to-cart adds
     * — and this element repaints from the `change` that click fires (see the
     * document listeners at the foot of this file). Nothing here repaints on
     * that path, deliberately: two paints from two sources is how a viewer
     * ends up showing a colour the page is not selling.
     *
     * Only when the page has no control for the option — a config whose
     * `hardware.option` names something that is not a variant option — is the
     * value kept here and painted on its own, so the row is never a set of
     * buttons that do nothing.
     */
    selectHardwareColor(value) {
      if (!value) return;
      const option = this.#config?.hardware?.option;
      if (!option) return;
      if (this.#activatePickerValue(option, value)) return;
      this.#hardwareOverride = value;
      this.refresh();
    }

    /**
     * Check the PDP's own input for `value` of `option`, through the SAME
     * container lookup the resolver reads selections back with (so the control
     * written to and the control read from are always the same one, quick-add
     * modals excluded on both sides).
     *
     * `.click()` on the radio FIRST, because it is what a shopper's click does
     * — checkedness first, then a bubbling `change` that Horizon's
     * <variant-picker> is listening for on itself — and it keeps this out of
     * the business of forging the event shape that listener reads.
     *
     * …and the checkedness is READ BACK, because the row's own home is a modal
     * dialog and `showModal()` makes the whole page behind it inert. A
     * synthetic click is script, not user interaction, so it is supposed to
     * land on inert content — but "supposed to" is thin ice to stand the one
     * control that changes the variant on, and the fallback (set it, then say
     * so) costs one comparison. A `<select>` has no native activation path at
     * all, so it is set and told either way.
     *
     * @returns {boolean} whether the page's picker owns the change now. True
     * also when the value is ALREADY selected: the control exists and says what
     * this asked for, so there is nothing to fall back to.
     */
    #activatePickerValue(option, value) {
      const container = window.bcVariantResolve?.findOptionContainer(document, option, {
        exclude: 'quick-add-dialog',
      });
      if (!container) return false;

      /** @type {NodeListOf<HTMLInputElement>} */
      const radios = container.querySelectorAll('input[type="radio"]');
      for (const input of radios) {
        if (input.value !== value) continue;
        if (!input.checked) input.click();
        if (!input.checked) {
          // The click did not take. Setting checkedness through the IDL still
          // unchecks the rest of the radio group, so the picker is left in a
          // state it could have reached itself — it just has to be told.
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }

      const select = /** @type {HTMLSelectElement|null} */ (container.querySelector('select'));
      if (select && [...select.options].some((entry) => entry.value === value)) {
        if (select.value !== value) {
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return true;
      }
      return false;
    }

    /**
     * The overlay's swatch row, pressed against whatever the metal is actually
     * painted with — which is the live picker read, not anything this row
     * remembers. So a colour changed from the PDP while the dialog is closed
     * (and a variant landing from anywhere else at all) leaves the row correct
     * the next time it opens, and the server's initial pressed state is only
     * ever confirmed rather than corrected.
     */
    #syncColors() {
      const value = this.#hardwareValue();
      for (const node of this.querySelectorAll('[data-bc-installed-color]')) {
        const on = node.getAttribute('data-bc-installed-color') === value;
        node.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
    }

    /* ---------------------------------------------------------------- *
     * Picking
     * ---------------------------------------------------------------- */

    /**
     * Pick a webbing. The chips are the compatibility ROSTER first and the
     * figure's control second, so a pick always moves the server-rendered
     * chrome — the pressed chip, the card, the pairing line, the caveat — and
     * only reaches the model when there IS one: on a figure nobody has pressed
     * "View in 3D" on, reading down the roster must not start a megabyte of
     * three.js and GLB behind a shopper who never asked to see it in 3D.
     */
    select(handle) {
      const entry = this.#config?.webbings?.[handle];
      if (!entry || handle === this.#handle) return;
      this.#handle = handle;
      this.#syncChrome();
      // A figure put back to its still is a picture again, and a pick there
      // swaps the picture (#syncPoster, above) — the model follows on the next
      // press, which shows whatever is picked by then.
      if (this.#boot && !this.#stillOnly) this.#build();
    }

    /** The still of the model a pick would render, or '' — #modelFor's own
     *  answer, so the picture before the press is the model after it. */
    #posterFor(handle) {
      const { key } = this.#modelFor(handle);
      const poster = key ? this.#config.models?.[key]?.poster : '';
      return typeof poster === 'string' ? poster : '';
    }

    /**
     * Point the stage's still at the current pick. The <img> is server markup
     * (the snippet writes it whenever any model carries a `poster`); this only
     * ever re-aims it, with the same hand-built `width=` set the snippet wrote
     * — a still is a raw Files url, so there is no image object to ask.
     * `srcset` goes on BEFORE `src`, or the browser starts the full-size fetch
     * and then throws it away. A pick whose model has no still hides the
     * element: the empty stage it always had beats another webbing's picture.
     */
    #syncPoster() {
      const img = /** @type {HTMLImageElement|null} */ (this.querySelector('[data-bc-installed-poster]'));
      if (!img) return;
      const url = this.#posterFor(this.#handle);
      img.hidden = !url;
      if (!url) return;
      const sized = (width) => `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
      const src = sized(1600);
      if (img.getAttribute('src') === src) return;
      const { key } = this.#modelFor(this.#handle);
      img.srcset = [480, 800, 1200, 1600].map((width) => `${sized(width)} ${width}w`).join(', ');
      img.src = src;
      img.alt = String(this.#config.models?.[key]?.alt || '').replace(' — interactive 3D model', '');
    }

    /**
     * The parts of the section that state the pick, every one of them server
     * markup this only ever toggles: the pressed chip, the detail card (which
     * is where the fitting note lives — the figure carries no caption of its
     * own, it would restate the card directly under it), which shackle pane is
     * shown, the expand overlay's own fitting line (the one place the card and
     * the pairing line are both off screen), and the caveat naming what is on
     * screen when the picked pairing has no model of its own yet.
     */
    #syncChrome() {
      const { key: shownKey, shackle, satisfied } = this.#modelFor(this.#handle);

      // `aria-pressed` is the whole selected state of a chip — the stylesheet
      // draws the pressed pill off this one attribute.
      for (const chip of this.querySelectorAll('[data-bc-installed-pick]')) {
        const on = chip.getAttribute('data-bc-installed-pick') === this.#handle;
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      // One card per COMPATIBLE webbing. The ruled-out ones carry no
      // `data-bc-installed-card` — they are never a pick and their card is
      // their only home — so this loop cannot reach them and their group stays
      // visible in both presentations.
      for (const card of this.querySelectorAll('[data-bc-installed-card]')) {
        card.hidden = card.getAttribute('data-bc-installed-card') !== this.#handle;
      }
      for (const pane of this.querySelectorAll('[data-bc-installed-pair]')) {
        pane.hidden = pane.getAttribute('data-bc-installed-pair') !== shackle;
      }
      // The expand overlay's fitting line — the same toggle keyed by HANDLE
      // rather than by shackle, because what a webbing needs to go in ("thread
      // the lock's 20 mm side", "double-wrap the lock") is the webbing's, not
      // its pairing's. Only webbings the roster says something about have a
      // pane at all, so a pick with nothing to state simply leaves them all
      // hidden. These live inside the dialog, which is display:none while
      // closed — picking from the in-page chip strip still sets them, and
      // the state is already right the next time it opens.
      for (const pane of this.querySelectorAll('[data-bc-installed-fit]')) {
        pane.hidden = pane.getAttribute('data-bc-installed-fit') !== this.#handle;
      }
      // The still in the stage — the picture of what the press would bring up.
      this.#syncPoster();

      // The overlay's anodize row states the PDP's selection, not the pick —
      // it is here so that the one call the element makes at upgrade leaves
      // EVERY server-rendered state confirmed against the live page, the same
      // reason the chips and cards are re-stated above.
      this.#syncColors();

      // Every caveat node, not the first: there are TWO — one under the figure
      // and one in the expand overlay, which carries its own copy of the chip
      // picker and so can reach a pending pairing from a place the figure's own
      // line is not visible from. Both hang off the same attribute and hold the
      // same sentence (Liquid writes it once), so this loop is the whole
      // difference between one of them being right and both of them being.
      for (const node of this.querySelectorAll('[data-bc-installed-caveat]')) {
        const caveat = /** @type {HTMLElement} */ (node);
        caveat.hidden = satisfied;
        if (satisfied) continue;
        const template = caveat.getAttribute('data-bc-installed-caveat') || '';
        caveat.textContent = template
          .replace('{shown}', this.#config.shackles?.[this.#modelShackle(shownKey)]?.label || '')
          .replace('{pairing}', this.#config.shackles?.[shackle]?.label || '');
      }
    }

    /* ---------------------------------------------------------------- *
     * Arming, expanding, gestures
     * ---------------------------------------------------------------- */

    #interactive() {
      return this.#armed || this.#expanded;
    }

    #syncTouchAction() {
      const canvas = this.#viewer?.renderer.domElement;
      if (canvas) canvas.style.touchAction = this.#interactive() ? 'none' : '';
    }

    #setArmed(on) {
      if (this.#armed === on) return;
      // Only ARMING is refused after a failure: a figure that fails while armed
      // has to be able to give the wheel back (#fail disarms it), and the old
      // single guard returned before it could.
      if (on && this.#failed) return;
      this.#armed = on;
      if (on) this.dataset.bcArmed = '';
      else delete this.dataset.bcArmed;
      this.#syncTouchAction();
    }

    /**
     * Arm because a press asked for it — the shopper asked to handle the model,
     * and making them press a second time to be allowed to would read as the
     * control not having worked.
     *
     * ONLY IF THEY STILL WANT IT: `#wantArm` is cleared by Escape, by a tap off
     * the figure and by the disarm ×, so a shopper who pressed and scrolled on
     * does not have a figure arm itself when the GLB lands.
     *
     * And move focus onto Expand, because the badge that was just pressed is
     * `display: none` the instant it arms — keyboard focus would otherwise fall
     * to <body>, leaving the shopper at the top of the document.
     */
    #armFromPress() {
      if (!this.#wantArm || this.#armed) return;
      this.#setArmed(true);
      if (!this.#armed) return; // #fail() got there first
      /** @type {HTMLElement|null} */ (this.querySelector('[data-bc-installed-expand]'))?.focus({
        preventScroll: true,
      });
    }

    #onClick = (event) => {
      const target = /** @type {Element|null} */ (event.target);
      if (!target?.closest) return;

      // The chips only. The cards carry no control at all — their product name
      // is an ordinary link and a click on it is a navigation, which never
      // reaches this branch.
      const chip = target.closest('[data-bc-installed-pick]');
      if (chip) {
        event.preventDefault();
        this.select(chip.getAttribute('data-bc-installed-pick') || '');
        return;
      }
      // …and the overlay's anodize swatches, which pick a colour the same way
      // the chips pick a webbing — except that this one is handed to the PDP's
      // own picker rather than kept here.
      const swatch = target.closest('[data-bc-installed-color]');
      if (swatch) {
        event.preventDefault();
        this.selectHardwareColor(swatch.getAttribute('data-bc-installed-color') || '');
        return;
      }
      if (target.closest('[data-bc-installed-arm]')) {
        event.preventDefault();
        // LOADER FIRST, GATE AFTER. On a figure nobody has pressed yet this is
        // what starts three.js, the GLB and the WebGL context, and it arms the
        // result; once loaded it is the gesture gate the badge always was. The
        // intent is recorded rather than acted on, because the arming may be
        // seconds away (see #armFromPress).
        this.#wantArm = true;
        // Back from the still first (a no-op on a figure that never went
        // there), so the `ready` #build is about to state is allowed to land.
        this.#toModel();
        this.#build().then((viewer) => {
          if (viewer) this.#armFromPress();
        });
        return;
      }
      if (target.closest('[data-bc-installed-expand]')) {
        event.preventDefault();
        this.#expand();
        return;
      }
      // The × in the armed controls row: "put the picture back" where there is
      // one (#toStill disarms on its way), and only the release it always was
      // where there is not.
      if (target.closest('[data-bc-installed-disarm]')) {
        event.preventDefault();
        this.#wantArm = false;
        if (this.#toStill()) return;
        this.#setArmed(false);
        this.#viewer?.homeFraming();
      }
    };

    /**
     * BACK TO THE STILL — the × and Escape, and nothing else. Same move as
     * assets/bc-rig-figure.js's #toStill: `data-bc-live` and the state come
     * off (the stylesheet cross-fades still and canvas), the loop stops, and
     * the pose goes HOME so the next press opens on the view the still is a
     * picture of rather than wherever the model was left.
     *
     * NOTHING IS TORN DOWN. The renderer, the context, the environment and the
     * parsed models all stay, `#boot` is resolved, and the next press is
     * #toModel plus one frame.
     *
     * Only when the current pick HAS a still: without one this would swap a
     * parked model for an empty box, and the × stays the plain release.
     * @returns {boolean} whether the figure went back to its still.
     */
    #toStill() {
      if (!this.#viewer || this.#stillOnly || this.#expanded || this.#failed) return false;
      if (!this.#posterFor(this.#handle) || !this.querySelector('[data-bc-installed-poster]')) return false;
      this.#wantArm = false;
      this.#setArmed(false);
      this.#stillOnly = true;
      delete this.dataset.bcLive;
      this.#setState('');
      const entry = this.#models.get(this.#modelKey);
      if (entry) this.#viewer.pivot.quaternion.copy(entry.home);
      this.#rehome = true;
      // The flag is already set, so the repaint homeFraming asks for is OWED
      // rather than drawn: syncRenderLoop answers "no loop", #toModel pays it.
      this.#viewer.homeFraming();
      this.syncRenderLoop();
      /** @type {HTMLElement|null} */ (this.querySelector('[data-bc-installed-arm]'))?.focus({
        preventScroll: true,
      });
      return true;
    }

    /** …and back to the model: the press that re-arms a figure the × put away.
     *  #build (the caller's next line) restates `ready` — at once for the model
     *  already on stage, after its load for a pick made while the still was up. */
    #toModel() {
      if (!this.#stillOnly) return;
      this.#stillOnly = false;
      // Loop first (so the viewer has one to draw in), then the frame it owes:
      // the box may have reflowed while the canvas was hidden.
      this.syncRenderLoop();
      this.#viewer?.resize();
    }

    /**
     * Full-window overlay — a <dialog> opened with showModal(), NOT the
     * Fullscreen API (iOS Safari does not support it for non-video elements).
     * The stage is re-parented in and back out, so the GL context and the loaded
     * GLBs survive the trip.
     */
    #expand() {
      const dialog = /** @type {HTMLDialogElement|null} */ (this.querySelector('dialog[data-bc-installed-dialog]'));
      const target = dialog?.querySelector('[data-bc-installed-dialog-stage]');
      const stage = this.querySelector('[data-bc-installed-stage]');
      if (!dialog || !target || !stage) return;

      // The expand control only exists once the figure is armed, so a viewer is
      // always already here — but expanding IS another explicit ask, so if it
      // ever is not, load rather than open an empty overlay. The dialog opens
      // now either way: its own "Loading…" line reads off `data-bc-state`, and
      // #showModel resizes and reveals into whichever box the stage is in.
      if (!this.#boot) this.#build();

      this.#expanded = true;
      target.appendChild(stage);
      dialog.showModal();
      this.#lockScroll(true);
      this.#syncTouchAction();
      // The overlay outranks the observer: the figure's own box may be scrolled
      // off behind a full-window dialog, and the loop has to run for what is on
      // top of it.
      this.syncRenderLoop();
      requestAnimationFrame(() => this.#viewer?.resize());
      /** @type {HTMLElement|null} */ (dialog.querySelector('[data-bc-installed-close]'))?.focus({
        preventScroll: true,
      });

      if (!dialog.dataset.bcWired) {
        dialog.dataset.bcWired = '1';
        dialog.querySelector('[data-bc-installed-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('close', () => this.#collapse());
        // …and the `open` attribute as well, because the `close` event is the
        // ONE signal here that cannot be relied on: it is absent in at least
        // one Chrome build (verified while building this), and losing it would
        // strand the canvas inside a closed dialog with the page still
        // scroll-locked. The attribute is definitive — Escape, close(), the
        // close button and a form[method=dialog] submit all clear it —
        // and #collapse is a no-op when it is not expanded, so the two
        // signals can both fire.
        new MutationObserver(() => {
          if (!dialog.open) this.#collapse();
        }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }
    }

    #collapse() {
      if (!this.#expanded) return;
      this.#expanded = false;
      this.#lockScroll(false);
      // Framing (distance and pan), not orientation: a crop taken in a
      // full-window overlay is wrong in a panel a third of the size, but the
      // face they turned toward themselves is theirs to keep.
      this.#viewer?.homeFraming();
      const stage = this.querySelector('[data-bc-installed-stage]');
      const home = this.querySelector('[data-bc-installed-stage-home]');
      if (stage && home) home.appendChild(stage);
      this.#syncTouchAction();
      // Back in the flow, so the observer's answer governs again — which may be
      // "off screen" if the page moved under the overlay.
      this.syncRenderLoop();
      requestAnimationFrame(() => this.#viewer?.resize());
      /** @type {HTMLElement|null} */ (this.querySelector('[data-bc-installed-expand]'))?.focus({
        preventScroll: true,
      });
    }

    /**
     * Horizon locks root scrolling with an attribute on <html> (base.css
     * `html[scroll-lock]`). Its owner bookkeeping lives in a module a classic
     * script cannot import, so mirror the attribute — and never clear it while
     * another dialog still wants it.
     */
    #lockScroll(on) {
      if (on) {
        document.documentElement.setAttribute('scroll-lock', '');
        return;
      }
      // Any OTHER open modal keeps the lock (ours has already lost its `open`
      // by the time collapse runs, so a match here is always someone else's).
      if (document.querySelector('dialog[open]')) return;
      document.documentElement.removeAttribute('scroll-lock');
    }

    #wireGestures(viewer) {
      const canvas = viewer.renderer.domElement;
      /** @type {Map<number, {x: number, y: number}>} */
      const pointers = new Map();
      let pinchDistance = 0;
      let midX = 0;
      let midY = 0;

      const spread = () => {
        const [a, b] = [...pointers.values()];
        return Math.hypot(a.x - b.x, a.y - b.y);
      };
      const midpoint = () => {
        const [a, b] = [...pointers.values()];
        return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      };

      /**
       * A one-pointer drag pans while the RIGHT button is down or Shift is held
       * — two ways in, because a right-drag on a trackpad is a contortion. It
       * LATCHES for the rest of the drag: a shift-drag actually ends by letting
       * go of Shift a moment before the button, which would otherwise spin the
       * model on the last few pixels of every pan.
       */
      let panDrag = false;
      const panning = (event) => {
        panDrag ||= Boolean(event.buttons & 2) || event.shiftKey;
        return panDrag;
      };

      canvas.addEventListener('pointerdown', (event) => {
        if (!this.#interactive()) return;
        canvas.setPointerCapture(event.pointerId);
        if (pointers.size === 0) panDrag = false;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        panning(event);
        if (pointers.size === 2) {
          pinchDistance = spread();
          ({ x: midX, y: midY } = midpoint());
        }
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!this.#interactive() || !pointers.has(event.pointerId)) return;
        const previous = pointers.get(event.pointerId);
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        // Two fingers are ONE gesture read two ways off the same pair: what the
        // spread does is zoom, what the midpoint does is pan.
        if (pointers.size >= 2) {
          const next = spread();
          const mid = midpoint();
          if (pinchDistance > 0 && next > 0) viewer.zoomBy(pinchDistance / next);
          pinchDistance = next;
          viewer.panBy(mid.x - midX, mid.y - midY);
          midX = mid.x;
          midY = mid.y;
          return;
        }

        if (panning(event)) {
          viewer.panBy(event.clientX - previous.x, event.clientY - previous.y);
          return;
        }

        // Radians-ish per canvas height, so the feel is size-independent.
        const scale = 3.2 / (canvas.clientHeight || 1);
        viewer.tumble((event.clientX - previous.x) * scale, (event.clientY - previous.y) * scale);
      });

      const release = (event) => {
        pointers.delete(event.pointerId);
        if (pointers.size < 2) pinchDistance = 0;
        if (pointers.size === 0) panDrag = false;
      };
      canvas.addEventListener('pointerup', release);
      canvas.addEventListener('pointercancel', release);

      // A right-drag is a pan, so the menu would land at the end of every one.
      // An un-armed canvas is a picture, and a picture keeps the browser's menu.
      canvas.addEventListener('contextmenu', (event) => {
        if (!this.#interactive()) return;
        event.preventDefault();
      });

      // Exponential, so the perceived step is the same 50 mm or 500 mm out, and
      // it can never cross zero. Not passive: an armed model owns the wheel.
      canvas.addEventListener(
        'wheel',
        (event) => {
          if (!this.#interactive()) return;
          event.preventDefault();
          viewer.zoomBy(Math.exp(event.deltaY * 0.0007));
        },
        { passive: false }
      );
    }

    /**
     * Called by the document-level listeners below. It also withdraws the
     * PENDING intent, which is the whole of the "pressed it, then scrolled on"
     * case: the load carries on (the bytes are already in flight and the model
     * is worth having if they come back), it simply does not seize the wheel
     * when it lands.
     */
    releaseIfArmed() {
      if (this.#expanded) return;
      this.#wantArm = false;
      if (!this.#armed) return;
      this.#setArmed(false);
      this.#viewer?.homeFraming();
    }

    /**
     * ESCAPE, the keyboard's ×: back to the still where there is one, the plain
     * release otherwise. Same precondition as the button it stands in for —
     * armed and inline — so an Escape meant for something else on the page
     * cannot fold up a figure nobody is handling, and an Escape out of the
     * expand overlay is left to the dialog.
     */
    dismissIfArmed() {
      if (this.#expanded) return;
      this.#wantArm = false;
      if (!this.#armed) return;
      if (this.#toStill()) return;
      this.#setArmed(false);
      this.#viewer?.homeFraming();
    }

    /**
     * Re-paint the metal when the PDP variant changes — and re-press the
     * overlay's swatch row with it, whether or not a model is on stage yet
     * (the row is server markup that exists from the first paint, and the
     * dialog it lives in can be opened the moment the figure is armed).
     */
    refresh() {
      this.#syncColors();
      if (this.#modelKey) this.#paint();
    }
  }

  customElements.define('bc-installed-diagram', BcInstalledDiagram);

  const each = (fn) => {
    for (const el of document.querySelectorAll('bc-installed-diagram')) fn(/** @type {any} */ (el));
  };

  // Tapping anywhere off the figure releases it, so the page gets its scroll
  // back without the shopper having to find the × first.
  document.addEventListener('click', (event) => {
    const target = /** @type {Element|null} */ (event.target);
    if (target?.closest?.('bc-installed-diagram')) return;
    each((el) => el.releaseIfArmed());
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') each((el) => el.dismissIfArmed());
  });

  // A backgrounded tab gets no rAF callbacks anyway in every current browser,
  // but "anyway" is the browser's promise, not ours: a loaded figure left in a
  // background tab should hold nothing but memory, and this is what says so.
  // One listener for every figure rather than one per element, so nothing has
  // to be unhooked when a morph moves a node.
  document.addEventListener('visibilitychange', () => each((el) => el.syncRenderLoop()));

  // Same pair every other variant-reactive bc surface listens on: `change`
  // catches the pick itself (fired before any morph) and `variant:update` covers
  // picker morphs that replace the inputs (re-emitted from Shopify's hosted
  // events by assets/bc-events-compat.js under Horizon v4).
  document.addEventListener('change', (event) => {
    if (/** @type {Element} */ (event.target)?.closest?.('fieldset.variant-option, .variant-option--dropdowns')) {
      each((el) => el.refresh());
    }
  });
  document.addEventListener('variant:update', () => each((el) => el.refresh()));
})();
